<template>
  <div
    v-editable="blok"
    class="video-player"
    :style="[widthResponsiveStyle]"
    :class="[positionInsideElementClass, videoWidthClass]"
  >
    <video
      ref="videoRef"
      class="video"
      :class="isMovableClass"
      :style="[
        moveXorPaddingStyle,
        moveYorPaddingStyle,
        borderRadiusStyle,
        borderStyle,
      ]"
      :src="videoSrc"
      :poster="posterSrc"
      autoplay
      loop
      muted
      playsinline
    />
  </div>
</template>

<script lang="ts" setup>
import type {
  SbInlineVideoPlayerStoryblok,
  SbPluginResponsiveSpacingNumbers,
} from '@/types'

interface Props {
  blok: SbInlineVideoPlayerStoryblok
  cardPaddingData?: SbPluginResponsiveSpacingNumbers
}

const props = defineProps<Props>()
const videoRef = ref<HTMLVideoElement | null>(null)

// Computed properties for video sources
const videoSrc = computed(() => props.blok.video?.filename)
const posterSrc = computed(() => props.blok.poster?.filename)

// Width and positioning classes
const videoWidthClass = computed(() =>
  props.blok.force_width ? 'video-player__width' : 'video-player__max-width',
)

const borderRadiusStyle = computed(() => ({
  '--border-radius-mobile': props.blok.border_radius?.mobile
    ? `${props.blok.border_radius.mobile}px`
    : '0',
  '--border-radius-tablet': props.blok.border_radius?.tablet
    ? `${props.blok.border_radius.tablet}px`
    : '0',
  '--border-radius-desktop': props.blok.border_radius?.desktop
    ? `${props.blok.border_radius.desktop}px`
    : '0',
}))

const widthResponsiveStyle = computed(() => ({
  '--width-mobile': props.blok.max_width_responsive?.mobile
    ? `${props.blok.max_width_responsive.mobile}px`
    : '100%',
  '--width-tablet': props.blok.max_width_responsive?.tablet
    ? `${props.blok.max_width_responsive.tablet}px`
    : '100%',
  '--width-desktop': props.blok.max_width_responsive?.desktop
    ? `${props.blok.max_width_responsive.desktop}px`
    : '100%',
}))

const moveXorPaddingStyle = computed(() => {
  if (props.cardPaddingData) {
    return {
      '--x-mobile': props.blok.move_x?.mobile
        ? `${props.blok.move_x.mobile}px`
        : `${props.cardPaddingData.mobile}px`,
      '--x-tablet': props.blok.move_x?.tablet
        ? `${props.blok.move_x.tablet}px`
        : `${props.cardPaddingData.tablet}px`,
      '--x-desktop': props.blok.move_x?.desktop
        ? `${props.blok.move_x.desktop}px`
        : `${props.cardPaddingData.desktop}px`,
    }
  }
  return {
    '--x-mobile': props.blok.move_x?.mobile
      ? `${props.blok.move_x.mobile}px`
      : '0',
    '--x-tablet': props.blok.move_x?.tablet
      ? `${props.blok.move_x.tablet}px`
      : '0',
    '--x-desktop': props.blok.move_x?.desktop
      ? `${props.blok.move_x.desktop}px`
      : '0',
  }
})

const moveYorPaddingStyle = computed(() => {
  if (props.cardPaddingData) {
    return {
      '--y-mobile': props.blok.move_y?.mobile
        ? `${props.blok.move_y.mobile}px`
        : `${props.cardPaddingData.mobile}px`,
      '--y-tablet': props.blok.move_y?.tablet
        ? `${props.blok.move_y.tablet}px`
        : `${props.cardPaddingData.tablet}px`,
      '--y-desktop': props.blok.move_y?.desktop
        ? `${props.blok.move_y.desktop}px`
        : `${props.cardPaddingData.desktop}px`,
    }
  }
  return {
    '--y-mobile': props.blok.move_y?.mobile
      ? `${props.blok.move_y.mobile}px`
      : '0',
    '--y-tablet': props.blok.move_y?.tablet
      ? `${props.blok.move_y.tablet}px`
      : '0',
    '--y-desktop': props.blok.move_y?.desktop
      ? `${props.blok.move_y.desktop}px`
      : '0',
  }
})

const isMovableClass = computed(() =>
  props.blok.is_movable ? 'video--movable' : '',
)

const positionInsideElementClass = computed(() =>
  props.blok.position?.position
    ? `position__${props.blok.position.position}`
    : '',
)
const bgColor = useState('bgColor')

const borderStyle = computed(() => ({
  outline: `2px solid ${bgColor.value}`,
  outlineOffset: '-2px',
  border: `1px solid ${bgColor.value}`,
}))
// Handle video playback
onMounted(() => {
  if (videoRef.value && props.blok.autoplay) {
    videoRef.value.play().catch((error) => {
      console.warn('Autoplay failed:', error)
    })
  }
})
</script>

<style lang="scss" scoped>
.video-player {
  --width-mobile: 100%;
  --width-tablet: 100%;
  --width-desktop: 100%;

  display: flex;
  flex-shrink: 0;

  &__width {
    width: var(--width-mobile);

    @media (min-width: $breakpoint-md) {
      width: var(--width-tablet);
    }

    @media (min-width: $breakpoint-lg) {
      width: var(--width-desktop);
    }
  }

  &__max-width {
    max-width: var(--width-mobile);

    @media (min-width: $breakpoint-md) {
      max-width: var(--width-tablet);
    }

    @media (min-width: $breakpoint-lg) {
      max-width: var(--width-desktop);
    }
  }
}

.video {
  --x-mobile: 0;
  --x-tablet: 0;
  --x-desktop: 0;
  --y-mobile: 0;
  --y-tablet: 0;
  --y-desktop: 0;
  --border-radius-mobile: 0;
  --border-radius-tablet: 0;
  --border-radius-desktop: 0;

  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: var(--border-radius-mobile);

  @media (min-width: $breakpoint-md) {
    border-radius: var(--border-radius-tablet);
  }

  @media (min-width: $breakpoint-lg) {
    border-radius: var(--border-radius-desktop);
  }
  &--movable {
    transform: translate(var(--x-mobile), var(--y-mobile));

    @media (min-width: $breakpoint-md) {
      transform: translate(var(--x-tablet), var(--y-tablet));
    }

    @media (min-width: $breakpoint-lg) {
      transform: translate(var(--x-desktop), var(--y-desktop));
    }
  }
}

.position {
  &__right-top {
    margin-left: auto;
    margin-bottom: auto;
  }

  &__right-bottom {
    margin-left: auto;
    margin-top: auto;
  }

  &__right {
    margin-left: auto;
    margin-top: auto;
    margin-bottom: auto;
  }

  &__left-top {
    margin-right: auto;
    margin-bottom: auto;
  }

  &__left-bottom {
    margin-right: auto;
    margin-top: auto;
  }

  &__left {
    margin-right: auto;
    margin-top: auto;
    margin-bottom: auto;
  }

  &__center {
    margin: auto;
  }

  &__top {
    margin-right: auto;
    margin-left: auto;
    margin-bottom: auto;
  }

  &__bottom {
    margin-right: auto;
    margin-left: auto;
    margin-top: auto;
  }

  &__off {
    margin: 0;
  }
}
</style>
